// Diccionarios de traducción de la bandeja de solicitudes sobre datos personales.
//
// Regla del repo: al usuario NUNCA se le muestra un enum crudo ('en_revision',
// 'formulario_publico', 'attention_voucher'). Todo pasa por acá. Si llega un
// valor que no conocemos, devolvemos una etiqueta genérica digna — nunca el
// string técnico.

export const TIPO_LABELS = {
  acceso: 'Acceso a sus datos',
  rectificacion: 'Corrección de datos',
  supresion: 'Eliminación de datos',
  oposicion: 'Oposición al uso',
  portabilidad: 'Entrega de una copia',
  bloqueo: 'Suspensión del uso',
};

// Explicación corta de qué pide la persona. Se usa en el detalle, para que quien
// resuelve no tenga que saberse la ley de memoria.
export const TIPO_DESCRIPCIONES = {
  acceso: 'La persona quiere saber qué datos suyos tenemos y cómo los usamos.',
  rectificacion: 'La persona pide corregir datos suyos que están equivocados o incompletos.',
  supresion: 'La persona pide que eliminemos sus datos.',
  oposicion: 'La persona pide que dejemos de usar sus datos para ciertos fines.',
  portabilidad: 'La persona pide una copia de sus datos en un formato que pueda reutilizar.',
  bloqueo: 'La persona pide que suspendamos temporalmente el uso de sus datos.',
};

export const ESTADO_LABELS = {
  recibida: 'Recibida',
  en_revision: 'En revisión',
  bloqueada: 'Uso suspendido',
  resuelta: 'Resuelta',
  rechazada: 'Rechazada',
};

export const ESTADO_COLORS = {
  recibida: 'blue',
  en_revision: 'gold',
  bloqueada: 'orange',
  resuelta: 'green',
  rechazada: 'red',
};

export const CANAL_LABELS = {
  autoservicio: 'Desde su cuenta',
  formulario_publico: 'Formulario público',
  correo: 'Correo electrónico',
};

// Estados en los que la solicitud ya está cerrada y el plazo deja de correr.
// Se importa ESTADOS_CERRADOS desde api/dataRequests para no duplicar la lista.

// Tipos de evento de la línea de tiempo. El backend puede sumar tipos nuevos sin
// avisarle al front, así que el fallback es deliberadamente neutro: la fila
// igual muestra el `detalle`, que es donde está la información real.
export const EVENTO_LABELS = {
  creada: 'Solicitud recibida',
  recibida: 'Solicitud recibida',
  estado_cambiado: 'Cambio de estado',
  cambio_estado: 'Cambio de estado',
  resuelta: 'Solicitud resuelta',
  rechazada: 'Solicitud rechazada',
  bloqueada: 'Uso de datos suspendido',
  bloqueo: 'Uso de datos suspendido',
  anonimizada: 'Datos anonimizados',
  anonimizacion: 'Datos anonimizados',
  informe_generado: 'Informe de datos generado',
  nota: 'Nota interna',
};

// Nombres humanos de los orígenes donde aparecen los datos de la persona.
// `tabla` es un nombre técnico de base de datos: jamás se muestra tal cual.
//
// Estos 11 valores son los que hoy emite el localizador (titular.srv.ts). Del
// lado del backend `tabla` está tipado como `string`, no como unión: el set es
// cerrado por convención, no por el compilador. Por eso el fallback importa.
export const TABLA_LABELS = {
  user: 'Cuenta de usuario',
  client: 'Ficha de cliente',
  administrador: 'Administrador de edificio',
  especialistas: 'Ficha de técnico',
  mantencion: 'Parte de mantención',
  attention_voucher: 'Comprobante de atención',
  order: 'Orden de trabajo',
  solicitud_ejecutivo: 'Solicitud de servicio',
  actividad_hallazgo: 'Hallazgo en una mantención',
  reporte_visita: 'Reporte de visita',
  incident_reports: 'Reporte de incidencia',
};

// Papel que cumple la persona en ese registro. `rol` sí es una unión real de 5
// valores en el backend (RolTitular), así que este diccionario es exhaustivo.
export const ROL_LABELS = {
  titular_cuenta: 'Titular de la cuenta',
  tecnico_responsable: 'Técnico responsable',
  recepcionista: 'Quien recibió el trabajo',
  cliente: 'Cliente',
  reportero: 'Quien reportó',
};

// El backend puede marcar requiereRevisionHumana sin mandar motivoRevision (pasa
// cuando el dato está embebido en un texto libre o en un JSON). Ese caso es
// frecuente, no excepcional, así que necesita su propio texto.
export const MOTIVO_REVISION_FALLBACK =
  'El dato aparece dentro de un texto escrito a mano, no en un campo propio. Conviene leer el registro completo antes de anonimizar.';

// Nombres humanos de los campos que el informe puede mostrar. Los que no estén
// acá se muestran con un formato legible genérico (ver humanizarCampo).
export const CAMPO_LABELS = {
  id: 'Identificador',
  rut: 'RUT',
  rut_recep: 'RUT de quien recibió',
  name_recep: 'Nombre de quien recibió',
  nombre: 'Nombre',
  fullname: 'Nombre completo',
  apellido: 'Apellido',
  email: 'Correo electrónico',
  correo: 'Correo electrónico',
  telefono: 'Teléfono',
  phone: 'Teléfono',
  direccion: 'Dirección',
  address: 'Dirección',
  comuna: 'Comuna',
  ciudad: 'Ciudad',
  cargo: 'Cargo',
  firma: 'Firma',
  observacion: 'Observación',
  observaciones: 'Observaciones',
  detalle: 'Detalle',
  createdAt: 'Fecha de creación',
  updatedAt: 'Última actualización',
};

const GENERICO = {
  tipo: 'Otro tipo de solicitud',
  estado: 'Sin estado',
  canal: 'Otro medio',
  evento: 'Actualización',
  tabla: 'Otros registros',
  rol: 'Persona mencionada',
};

const lookup = (dict, value, fallback) => {
  if (value === null || value === undefined || value === '') return fallback;
  return dict[value] ?? fallback;
};

export const tipoLabel = (v) => lookup(TIPO_LABELS, v, GENERICO.tipo);
export const tipoDescripcion = (v) => TIPO_DESCRIPCIONES[v] ?? null;
export const estadoLabel = (v) => lookup(ESTADO_LABELS, v, GENERICO.estado);
export const estadoColor = (v) => ESTADO_COLORS[v] ?? 'default';
export const canalLabel = (v) => lookup(CANAL_LABELS, v, GENERICO.canal);
export const eventoLabel = (v) => lookup(EVENTO_LABELS, v, GENERICO.evento);
export const tablaLabel = (v) => lookup(TABLA_LABELS, v, GENERICO.tabla);
export const rolLabel = (v) => lookup(ROL_LABELS, v, GENERICO.rol);

// Para los nombres de campo sí podemos degradar con elegancia: 'name_recep' ->
// 'Name recep' es feo pero no filtra jerga de sistema como una tabla. Aun así
// preferimos el diccionario.
export const humanizarCampo = (key) => {
  if (CAMPO_LABELS[key]) return CAMPO_LABELS[key];
  if (!key) return 'Dato';
  const limpio = String(key)
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim()
    .toLowerCase();
  return limpio.charAt(0).toUpperCase() + limpio.slice(1);
};

// Los valores de `campos` vienen como unknown: pueden ser objetos, arreglos,
// fechas o null. Nunca volcamos JSON crudo en pantalla.
export const formatearValorCampo = (valor) => {
  if (valor === null || valor === undefined || valor === '') return '—';
  if (typeof valor === 'boolean') return valor ? 'Sí' : 'No';
  if (typeof valor === 'number') return String(valor);
  if (typeof valor === 'string') return valor;
  if (Array.isArray(valor)) return `${valor.length} elemento${valor.length === 1 ? '' : 's'}`;
  if (typeof valor === 'object') return 'Información adicional';
  return String(valor);
};
